/* jshint esversion: 6, -W097 */
/* globals require, module */
'use strict';

FiltersSortByService.$inject = [
    '$q',
    '$rootScope',
    'CategoriesService',
    'CmsContentService',
    'UtilService',
    'AnalyticsService'
];
function FiltersSortByService(
    $q,
    $rootScope,
    CategoriesService,
    CmsContentService,
    UtilService,
    AnalyticsService
) {
    var _filtersSortByText = null;
    var _defaultFiltersSortByOption = null;
    var _currentSortName = {};
    var _filtersSortByOptionsByCode = {};
    var _activeSortOptions = [];
    var _translatedSortByOptions;
    var _sortByOptions = UtilService.getSiteSetting('sort_by_options');
    let _onSetActiveSortByOptionCallbacks = [];

    $rootScope.$on('categorySet', function(e, category) {
        // If the category is changed, assume that we're being taken to
        // the results page, so pass the `isFavoritedProductsPage`
        // parameter as `false`
        updateActiveSortOptions(false);
    });

    return {
        isSortByDropdownReady: false,
        getCategoryDefaultSortOption: getCategoryDefaultSortOption,
        getTranslatedSortByOptions: getTranslatedSortByOptions,
        getSwiftypeSort: getSwiftypeSort,
        constructActiveSortOption: constructActiveSortOption,
        updateActiveSortOptions: updateActiveSortOptions,
        getFiltersSortByOptions: getFiltersSortByOptions,
        getFiltersSortByText: getFiltersSortByText,
        getFiltersSortByOptionsByCode: getFiltersSortByOptionsByCode,
        getActiveSortByOption: getActiveSortByOption,
        getActiveSortByName: getActiveSortByName,
        setActiveSortByOption: setActiveSortByOption,
        onSetActiveSortByOption: onSetActiveSortByOption,
        getNewActiveSortBy: getNewActiveSortBy,
        getDefaultSortByAndReset: getDefaultSortByAndReset,
        trackProductSortEngaged: trackProductSortEngaged,
        translatedSortByKey: 'sortBy'
    };


    /**
     * Gets the default sort option per category setting.
     * @returns {*}
     */
    function getCategoryDefaultSortOption () {
        var currentCategory = CategoriesService.getActiveCategory();
        return currentCategory.default_sort;
    }

    /**
     * Gets the translated _sortByOptions once, storing them as a copy
     * of the original options hash, but with translated text.
     * @returns {$q.promise} Resolves to a hast table of translated sortBy options
     */
    function getTranslatedSortByOptions () {
        var deferred = $q.defer();
        if (!_translatedSortByOptions) {
            // If there are no translated sort options
            var sortByOptions = angular.copy(_sortByOptions.choices);
            for (var code in sortByOptions) {
                sortByOptions[code].code = code;
                sortByOptions[code].raw_code = code;
            }
            populateSortOptionsFromCMS(sortByOptions).then(function(translatedSortOptions) {
                _translatedSortByOptions = translatedSortOptions;
                deferred.resolve(_translatedSortByOptions);
            });
        } else {
            deferred.resolve(_translatedSortByOptions);
        }
        return deferred.promise;
    }

    /**
     * Gets the sort options when sending to Swiftype.
     *
     * @param sortBy: The field and direction to sort by, e.g. "-price".
     * @param params: Search params to update for sorting.
     * @param categoryCode: The category to add the fields to.
     * @return: The params with the adjusted sort fields.
     */
    function getSwiftypeSort (sortBy, params, categoryCode) {
        var field = null,
            direction = null;
        if (sortBy.startsWith('-')) {
            // direction is 'desc' by default.
            sortBy = sortBy.substr(1);
        } else {
            direction = 'asc';
        }
        switch (sortBy) {
            case 'energy_score':
                field = 'enervee_score';
                break;
            case 'relevance':
                field = sortBy;
                // relevance only has one direction.
                direction = null;
                break;
            case 'user_reviews':
                field = 'reviews_rank';
                break;
            default:
                field = sortBy;
                break;
        }

        if (field !== null) {
            if (!('sort_field' in params)) {
                params.sort_field = {};
            }
            params.sort_field[categoryCode] = field;
        }
        if (direction !== null) {
            if (!('sort_direction' in params)) {
                params.sort_direction = {};
            }
            params.sort_direction[categoryCode] = direction;
        }

        return params;
    }

    /**
     * Converts a list of sort option codes ['price', 'user_reviews', '-clear_cost',...]
     * into a list of sort option objects
     * [
     *      {'code': 'price', 'display_name': 'Price', 'asc_des_display_name': 'lowest'},
     *      {'code': 'user_reviews', 'display_name': 'User Reviews', 'asc_des_display_name': 'lowest'},
     *      {'code': '-clear_cost', 'display_name': 'Clear Cost', 'asc_des_display_name': 'highest'},
     *      ...
     * ]
     * and assign list to _activeSortOptions to be passed to FiltersSortByService.
     * Also sets up code to object hashtable of sortby options
     *
     * @param {String[]} sortOptionCodes - An array of the raw sortOption codes
     * @returns {$q.promise} Resolves when sortOptions are fully constructed and translated
     */
    function constructActiveSortOption (sortOptionCodes) {
        var deferred = $q.defer();
        getTranslatedSortByOptions().then(function(translatedSortOptions) {
            // If no array of sortOptionCodes is passed, just use all the
            // available options
            sortOptionCodes = sortOptionCodes || Object.keys(_sortByOptions.choices);
            _activeSortOptions = [];
            sortOptionCodes.forEach(function (code) {
                var option = translatedSortOptions[code];
                _activeSortOptions.push({
                    code: option.code,
                    raw_code: code,  // the original, untranslated code
                    display_name: option.display_name,
                    asc_des_display_name: option.asc_des_display_name
                });
            });

            // Set up _filtersSortByOptionsByCode
            _activeSortOptions.forEach(function(option) {
                _filtersSortByOptionsByCode[option.raw_code] = option;
            });
            if (_defaultFiltersSortByOption === null) {
                _defaultFiltersSortByOption = getCategoryDefaultSortOption();
                _currentSortName.display_name = _defaultFiltersSortByOption;
            }

            deferred.resolve();
        }, function () {
            _activeSortOptions = [];
            _filtersSortByOptionsByCode = {};
        });

        return deferred.promise;
    }

    /**
     * Updates the sort option list with the current active category,
     * and creates a code to object mapping hashtable.
     *
     * @returns {$q.promise} Resolves when sortBy options have been updated
     */
    function updateActiveSortOptions (isFavoritedProductsPage) {
        var sortOptionCodes;
        // if request comes from favorited products page,
        // manually call constructActiveSortOption with all
        // sort options available to populate active sort options
        if (!isFavoritedProductsPage) {
            // else, use the active category
            sortOptionCodes = CategoriesService.getActiveCategory().sort_option_codes;
        }

        return constructActiveSortOption(sortOptionCodes);
    }

    /**
     * Helper function that, given a sort option object and an object
     * of translations, returns a new and translated sort option
     *
     * @param {Object} sortOption
     * @param {Object} translations - Hash table of translted sortBy options
     * @returns {Object} Translated sortBy option
     */
    function translateSortOption (sortOption, translations) {
        var option = JSON.parse(JSON.stringify(sortOption));
        var cmsKey = option.code.replace('-', '');
        if (cmsKey in translations) {
            option.display_name = translations[cmsKey];
        }
        var ascDes = option.asc_des_display_name;
        if (ascDes && ascDes in translations) {
            option.asc_des_display_name = translations[ascDes];
        }
        var code = option.code;
        var translatedCode = '';
        if (code[0] === '-') {
            code = code.substring(1);
            translatedCode = '-';
        }
        if (code && code in translations) {
            translatedCode += translations[code + '_code'];
            option.code = translatedCode;
        }
        return option;
    }

    /**
     * Helper function to fetch the translation for the given sortOption
     * code, if one exists
     * @returns {$q.promise} Resolves to the translated code, or an empty string
     */
    function getTranslatedSortCode (code) {
        var deferred = $q.defer();
        if (code[0] === '-'){
            code = code.substr(1);
        }
        CmsContentService.getMarketPlaceText('sort_by:' + code).then(function(text){
            var translated_code = text.toLowerCase().replace(new RegExp(' ', 'g'), '_').replace(new RegExp("'", "g"), "");
            deferred.resolve(translated_code);
        });
        return deferred.promise;
    }

    /**
     * Populates sort options based on the CMS-specified sort option
     * display names.
     *
     * @param {Object} sortByOptions - Hash table of sortBy options to translate
     * @returns {$q.promise} Resolves to a hash table of the translated sortBy options
     */
    function populateSortOptionsFromCMS (sortByOptions) {
        var deferred = $q.defer();
        var promises = {};

        for (var sortByIndex in sortByOptions) {
            var sortOption = sortByOptions[sortByIndex];
            var cmsKey = sortOption.code.replace('-', '');
            if (!(cmsKey in promises)) {
                promises[cmsKey] = CmsContentService.getMarketPlaceText('sort_by:' + cmsKey);
            }

            var ascDes = sortOption.asc_des_display_name;
            var ascDesDefaults = ["highest", "lowest"];
            if (ascDes && ascDesDefaults.indexOf(ascDes) > -1 && !(ascDes in promises)) {
                promises[ascDes] = CmsContentService.getMarketPlaceText('sort_by:' + ascDes);
            }

            var sort_option = sortOption.code;
            if (sort_option[0] === '-'){
                sort_option = sort_option.substr(1);
            }

            promises[sort_option + '_code'] = getTranslatedSortCode(sort_option);
        }
        $q.all(promises).then(function(translations) {
            for (var sortByIndex in sortByOptions) {
                var sortOption = sortByOptions[sortByIndex];
                sortByOptions[sortByIndex] = translateSortOption(sortOption, translations);
            }
            deferred.resolve(sortByOptions);
        });
        return deferred.promise;
    }

    /**
     * Helper function to return the active sortBy options
     *
     * @returns {$q.promise} Resolves to an array of the active sortBy options
     */
    function getFiltersSortByOptions () {
        // just needs to get the translated active sortby options
        var deferred = $q.defer();
        deferred.resolve(_activeSortOptions);
        return deferred.promise;
    }

    /**
     * Returns the current sort by text or retrieves from the CMS, then returns.
     *
     * @returns {$q.promise} Resolves to the sortBy text (from the CMS)
     */
    function getFiltersSortByText () {
        var deferred = $q.defer();
        if (_filtersSortByText === null) {
            CmsContentService.getMarketPlaceText('sort_by:text').then(function(text) {
                _filtersSortByText = text.replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
                    return index === 0 ? letter.toLowerCase() : letter.toUpperCase();
                }).replace(/\s+/g, '');
                deferred.resolve(_filtersSortByText);
            });
        } else {
            deferred.resolve(_filtersSortByText);
        }
        return deferred.promise;
    }

    /**
     * Gets the hashtable which will have a list of options codes to objects
     *
     * @returns {Object} Hash table of the active sortBy options
     */
    function getFiltersSortByOptionsByCode () {
        return _filtersSortByOptionsByCode;
    }

    /**
     * Gets the active filter sort by option's code
     *
     * @returns {String} The active sortBy option code
     */
    function getActiveSortByOption () {
        return _defaultFiltersSortByOption;
    }

    /**
     * Gets the full active filter sort by option object
     *
     * @returns {Object} The active sortBy option
     */
    function getActiveSortByName () {
        _currentSortName = _filtersSortByOptionsByCode[_defaultFiltersSortByOption];
        return _currentSortName;
    }

    /**
     * Sets the active filter sort by option
     *
     * @returns {undefined}
     */
    function setActiveSortByOption (defaultFiltersSortByOption) {
        if (defaultFiltersSortByOption) {
            _defaultFiltersSortByOption = defaultFiltersSortByOption;
        }

        runOnSetActiveSortCallbacks();
    }

    /**
     * Adds a callback to be invoked after the setActiveSortByOption is called
     *
     * @returns {undefined}
     */
    function onSetActiveSortByOption (callback) {
        if (angular.isFunction(callback)) {
            _onSetActiveSortByOptionCallbacks.push(callback);
        }
    }

    /**
     * Runs the callbacks registered to run after the setActiveSortByOption is called
     */
    function runOnSetActiveSortCallbacks() {
        if (_onSetActiveSortByOptionCallbacks.length > 0) {
            _onSetActiveSortByOptionCallbacks.forEach(callback => {
                callback()
            });
        }
    }

    /**
     * Handles edge case where a user switches from a category with an
     * active sort option that does not exist in the new category.
     * In such case, we default to `_sortByOptions.default`, otherwise
     * we return the current active sort option.
     *
     * @param {Object} newCategory - The new category object
     * @param {String} currentActiveSortBy - The current active sortBy option's code
     * @returns {String} The new active sortBy option code
     */
    function getNewActiveSortBy (newCategory, currentActiveSortBy) {
        if (newCategory.sort_option_codes.indexOf(currentActiveSortBy) === -1) {
            // new category does not have current sort option
            return getCategoryDefaultSortOption();
        }
        return currentActiveSortBy;
    }

    /**
     * Sets the sort by param to default and returns the display_name
     *
     * @returns {String|undefined} The active sortBy option's code, if it exists
     */
    function getDefaultSortByAndReset (newCategory) {
        var activeSortBy;
        if (newCategory) {
            activeSortBy = _filtersSortByOptionsByCode[newCategory.default_sort];
        } else {
            activeSortBy = _filtersSortByOptionsByCode[getCategoryDefaultSortOption()];
        }
        setActiveSortByOption(activeSortBy);
        if (activeSortBy) {
            return activeSortBy.code;
        }
        return "";
    }

    /**
     * Tracks the product sort engaged segment event
     *
     * @param sortKey {string} the selected sortkey
     */
    function trackProductSortEngaged (sortKey) {
        const activeCategory = CategoriesService.getActiveCategory();
        AnalyticsService.reportEvent('Product Sort Engaged', {
            category: activeCategory.code,
            sort_key: sortKey,
        });
    }
}

module.exports = FiltersSortByService;
